// src/services/SpeechRecognitionService.ts
import { transcribeAudioViaProxy } from './secureProxy';

interface SpeechRecognitionResult {
  text: string;
  confidence: number;
  isFinal: boolean;
  timestamp: Date;
}

class SpeechRecognitionService {
  private recognition: any = null;
  private isListening: boolean = false;
  private interimResults: string = '';
  private finalResults: string = '';
  private onResultCallback: ((result: SpeechRecognitionResult) => void) | null = null;
  private onErrorCallback: ((error: string) => void) | null = null;

  constructor() {
    this.initializeSpeechRecognition();
  }

  private getIsBrowser(): boolean {
    return typeof window !== 'undefined';
  }

  private initializeSpeechRecognition(): void {
    if (!this.getIsBrowser()) return;

    // Utiliser l'API Web Speech si disponible
    const SpeechRecognition =
      (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;

    if (!SpeechRecognition) {
      console.warn('Web Speech API non disponible. Utilisation du fallback.');
      return;
    }

    this.recognition = new SpeechRecognition();
    this.recognition.continuous = true;
    this.recognition.interimResults = true;
    this.recognition.lang = 'fr-FR';
    this.recognition.maxAlternatives = 1;

    this.recognition.onstart = () => {
      this.isListening = true;
      console.log('Reconnaissance vocale démarrée');
    };

    this.recognition.onresult = (event: any) => {
      let interimTranscript = '';
      let finalTranscript = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += transcript;
        } else {
          interimTranscript += transcript;
        }
      }

      this.interimResults = interimTranscript;

      if (finalTranscript) {
        this.finalResults += finalTranscript + ' ';

        const last = event.results[event.results.length - 1]?.[0];
        const result: SpeechRecognitionResult = {
          text: finalTranscript.trim(),
          confidence: (last?.confidence ?? 0.8),
          isFinal: true,
          timestamp: new Date()
        };

        this.onResultCallback?.(result);
      } else if (interimTranscript) {
        const result: SpeechRecognitionResult = {
          text: interimTranscript.trim(),
          confidence: 0.5,
          isFinal: false,
          timestamp: new Date()
        };

        this.onResultCallback?.(result);
      }
    };

    this.recognition.onerror = (event: any) => {
      console.error('Erreur reconnaissance vocale:', event?.error);
      this.isListening = false;

      if (this.onErrorCallback) {
        this.onErrorCallback(event?.error || 'unknown_error');
      }
    };

    this.recognition.onend = () => {
      this.isListening = false;
      console.log('Reconnaissance vocale terminée');
    };
  }

  // Démarrer l'écoute
  startListening(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.getIsBrowser()) {
        reject(new Error('Reconnaissance vocale non disponible (non-browser)'));
        return;
      }

      if (!this.recognition) {
        reject(new Error('Reconnaissance vocale non disponible'));
        return;
      }

      if (this.isListening) {
        resolve();
        return;
      }

      try {
        this.recognition.start();
        resolve();
      } catch (error: any) {
        // ✅ SAFE: certains navigateurs throw "InvalidStateError" si start appelé trop vite
        const name = error?.name || '';
        const msg = String(error?.message || '');
        if (name === 'InvalidStateError' || msg.toLowerCase().includes('already started')) {
          resolve();
          return;
        }
        reject(error);
      }
    });
  }

  // Arrêter l'écoute
  stopListening(): void {
    if (!this.getIsBrowser()) return;

    try {
      if (this.recognition && this.isListening) {
        this.recognition.stop();
        this.isListening = false;
      }
    } catch (e) {
      // no-op
    }
  }

  // Utiliser Whisper via proxy sécurisé Forfeo (aucune clé OpenAI côté navigateur)
  async transcribeWithWhisper(audioBlob: Blob): Promise<string> {
    try {
      const data = await transcribeAudioViaProxy(audioBlob, 'fr');
      return data?.text || data?.transcription || '';
    } catch (error) {
      console.error('Erreur transcription Whisper via proxy:', error);
      throw error;
    }
  }

  // Enregistrer l'audio depuis le microphone
  async recordAudio(duration: number = 10000): Promise<Blob> {
    return new Promise(async (resolve, reject) => {
      try {
        if (!this.getIsBrowser()) {
          reject(new Error('Enregistrement audio non disponible (non-browser)'));
          return;
        }

        if (!navigator?.mediaDevices?.getUserMedia) {
          reject(new Error('getUserMedia non supporté'));
          return;
        }

        if (typeof (window as any).MediaRecorder === 'undefined') {
          reject(new Error('MediaRecorder non supporté'));
          return;
        }

        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        const mediaRecorder = new MediaRecorder(stream);
        const audioChunks: BlobPart[] = [];

        mediaRecorder.ondataavailable = (event: any) => {
          if (event?.data) audioChunks.push(event.data);
        };

        mediaRecorder.onstop = () => {
          try {
            const audioBlob = new Blob(audioChunks, { type: 'audio/wav' });
            stream.getTracks().forEach((track) => track.stop());
            resolve(audioBlob);
          } catch (e) {
            stream.getTracks().forEach((track) => track.stop());
            reject(e);
          }
        };

        mediaRecorder.start();

        // Arrêter après la durée spécifiée
        const timer = window.setTimeout(() => {
          if (mediaRecorder.state === 'recording') {
            mediaRecorder.stop();
          }
        }, duration);

        // Option pour arrêter manuellement (conserve ton comportement existant)
        const stopRecording = () => {
          window.clearTimeout(timer);
          if (mediaRecorder.state === 'recording') {
            mediaRecorder.stop();
          }
        };

        (window as any).stopRecording = stopRecording;
      } catch (error) {
        reject(error);
      }
    });
  }

  // Définir les callbacks
  onResult(callback: (result: SpeechRecognitionResult) => void): void {
    this.onResultCallback = callback;
  }

  onError(callback: (error: string) => void): void {
    this.onErrorCallback = callback;
  }

  isSupported(): boolean {
    if (!this.getIsBrowser()) return false;
    return !!(window as any).SpeechRecognition || !!(window as any).webkitSpeechRecognition;
  }

  getIsListening(): boolean {
    return this.isListening;
  }

  getInterimResults(): string {
    return this.interimResults;
  }

  getFinalResults(): string {
    return this.finalResults;
  }

  reset(): void {
    this.interimResults = '';
    this.finalResults = '';
  }
}

export default new SpeechRecognitionService();
